<script setup>
/**
 * ItemDialogQtyStepper Component
 * Qty stepper with +/- buttons and numeric input
 * Supports boxSize step increments and onlyBox validation
 */
import { ref, watch } from 'vue'

const props = defineProps({
  qty: {
    type: Number,
    required: true,
  },
  step: {
    type: Number,
    default: 1,
  },
  min: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  onlyBox: {
    type: Boolean,
    default: false,
  },
  boxSize: {
    type: Number,
    default: 1,
  },
})

const emit = defineEmits(['update:qty', 'remove', 'invalid'])

// Local input value for free typing
const inputValue = ref(String(props.qty))

// Shake animation state
const isShaking = ref(false)

// Sync input when qty prop changes externally
watch(
  () => props.qty,
  (newQty) => {
    inputValue.value = String(newQty)
  }
)

// Effective step (fallback to 1 if invalid)
function getStep() {
  return props.step > 0 ? props.step : 1
}

// Effective boxSize for validation
function getBoxSize() {
  return props.boxSize > 0 ? props.boxSize : 1
}

// Trigger shake animation
function triggerShake() {
  isShaking.value = true
  setTimeout(() => {
    isShaking.value = false
  }, 400)
}

// Increment by step
function increment() {
  if (props.disabled) return
  const step = getStep()
  // If qty is 0, start with step (first add)
  const newQty = props.qty === 0 ? step : props.qty + step
  emit('update:qty', newQty)
}

// Decrement by step
function decrement() {
  if (props.disabled) return
  // Can't decrement from 0
  if (props.qty === 0) return
  const newQty = props.qty - getStep()
  if (newQty <= 0) {
    emit('remove')
  } else {
    emit('update:qty', newQty)
  }
}

// Focus clears the field so typing starts fresh (no erasing the old value
// first); leaving it empty restores the pre-focus qty (see commitInput).
function handleFocus() {
  if (props.disabled) return
  inputValue.value = ''
}

// Handle input change on blur/enter
function commitInput() {
  if (props.disabled) return

  // Left empty (typed nothing after the focus-clear, or erased everything) —
  // the user changed their mind: restore the current qty silently, no shake.
  if (String(inputValue.value).trim() === '') {
    inputValue.value = String(props.qty)
    return
  }

  const parsed = parseInt(inputValue.value, 10)

  // Invalid input - revert
  if (isNaN(parsed) || parsed < 0) {
    inputValue.value = String(props.qty)
    triggerShake()
    emit('invalid')
    return
  }

  // Zero - remove
  if (parsed === 0) {
    emit('remove')
    return
  }

  // OnlyBox validation: must be multiple of boxSize
  if (props.onlyBox) {
    const box = getBoxSize()
    if (parsed % box !== 0) {
      // Invalid - revert to last valid qty
      inputValue.value = String(props.qty)
      triggerShake()
      emit('invalid')
      return
    }
  }

  // Valid - emit the new qty
  emit('update:qty', parsed)
}

// Handle enter key
function handleKeydown(e) {
  if (e.key === 'Enter') {
    e.target.blur()
  }
}
</script>

<template>
  <div class="qty-stepper" :class="{ disabled, shaking: isShaking }">
    <!-- Decrement / Remove Button -->
    <button
      class="stepper-btn stepper-btn-minus"
      :disabled="disabled || qty === 0"
      @click="decrement"
      aria-label="הפחת כמות"
    >
      <!-- Trash icon when qty equals one step (will remove) -->
      <svg
        v-if="qty > 0 && qty <= step"
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
      </svg>
      <!-- Minus icon otherwise -->
      <svg
        v-else
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
      >
        <path d="M5 12h14" />
      </svg>
    </button>

    <!-- Qty Input -->
    <input
      type="text"
      inputmode="numeric"
      class="qty-input"
      :class="{ 'input-error': isShaking }"
      v-model="inputValue"
      :disabled="disabled"
      @focus="handleFocus"
      @blur="commitInput"
      @keydown="handleKeydown"
      aria-label="כמות"
    />

    <!-- Increment Button -->
    <button
      class="stepper-btn stepper-btn-plus"
      :disabled="disabled"
      @click="increment"
      aria-label="הוסף כמות"
    >
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
      >
        <path d="M12 5v14M5 12h14" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.qty-stepper {
  display: flex;
  align-items: center;
  gap: 2px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  overflow: hidden;
}

.qty-stepper.disabled {
  opacity: 0.5;
  pointer-events: none;
}

/* Shake Animation */
.qty-stepper.shaking {
  animation: shake 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97);
  border-color: var(--color-error);
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-4px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(4px);
  }
}

.stepper-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  background: transparent;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.stepper-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.stepper-btn:active:not(:disabled) {
  background: var(--color-primary);
  color: white;
}

.stepper-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.stepper-btn-minus:active:not(:disabled) {
  background: var(--color-error);
}

.qty-input {
  width: 56px;
  height: 44px;
  padding: 0;
  border: none;
  background: transparent;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family);
  color: var(--text-primary);
  text-align: center;
  outline: none;
  transition:
    background var(--transition-fast),
    color var(--transition-fast);
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.qty-input:focus {
  background: rgba(40, 53, 72, 0.08);
}

.qty-input.input-error {
  background: rgba(239, 68, 68, 0.1);
  color: var(--color-error);
}

/* Mobile & Tablet adjustments */
@media (max-width: 1279px) {
  .stepper-btn {
    width: 48px;
    height: 48px;
  }

  .qty-input {
    width: 60px;
    height: 48px;
    font-size: var(--font-size-xl);
  }
}
</style>
